import React, { useState, useRef } from 'react';
import { Camera, Upload, FileText, AlertCircle, CheckCircle, Clock, Users, Shield, Zap, Award, ArrowLeft, ArrowRight, Info, Star, TrendingUp } from 'lucide-react';
import { getCurrentTenantSlug, getTenantApiUrl } from '../../tenantConfig';
import './DentalCheckup.scss';

const DentalCheckup = () => {
  const [currentStep, setCurrentStep] = useState('upload'); // upload, analyzing, results, lead-capture
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  
  // Lead capture form state
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    wantsConsultation: false
  });
  
  const fileInputRef = useRef(null);
  const cameraRef = useRef(null);

  // Trust indicators data
  const trustIndicators = [
    { icon: <Users size={20} />, label: '50K+ Users', value: 'Trusted by thousands' },
    { icon: <Shield size={20} />, label: '98% Accuracy', value: 'AI-powered precision' },
    { icon: <Zap size={20} />, label: '2-3 Min', value: 'Lightning fast results' },
    { icon: <Award size={20} />, label: 'Certified', value: 'Medical grade AI' }
  ];

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError(null);
      
      // Show image quality warning
      if (file.size > 5 * 1024 * 1024) { // 5MB
        setError({
          message: 'Large image detected',
          tips: [
            '💡 Large images may take longer to process',
            '💡 Consider compressing for faster analysis',
            '💡 Ensure good lighting and focus for best results'
          ],
          details: 'Image size: ' + (file.size / (1024 * 1024)).toFixed(1) + 'MB'
        });
      }
    } else {
      setError('Please select a valid image file (JPG, PNG, JPEG)');
    }
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        setSelectedImage(file);
        setImagePreview(URL.createObjectURL(file));
        setError(null);
      } else {
        setError('Please select a valid image file (JPG, PNG, JPEG)');
      }
    }
  };

  // Handle camera capture
  const handleCameraCapture = () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          const video = cameraRef.current;
          video.srcObject = stream;
          video.play();
        })
        .catch(err => {
          setError('Unable to access camera: ' + err.message);
        });
    } else {
      setError('Camera not available on this device');
    }
  };

  // Capture photo from camera
  const capturePhoto = () => {
    const video = cameraRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob(blob => {
      const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(blob));
      
      // Stop camera stream
      const stream = video.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }, 'image/jpeg');
  };

  // Start analysis with progress simulation
  const startAnalysis = async () => {
    if (!selectedImage) return;
    
    setIsLoading(true);
    setCurrentStep('analyzing');
    setError(null);
    setAnalysisProgress(0);
    
    // Simulate progress for better UX
    const progressInterval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 500);
    
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      
      // Use tenant-based API URL
      const tenant = getCurrentTenantSlug();
      const baseUrl = getTenantApiUrl(tenant);
      const response = await fetch(`${baseUrl}/ai/dental-checkup/upload/`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAnalysisProgress(100);
        setAnalysisResults(data.data);
        setSessionId(data.session_id);
        setCurrentStep('results');
      } else {
        // Handle error with improved messaging
        let errorMessage = data.error || data.message || 'Analysis failed';
        let helpTips = data.help_tips || [];
        
        setError({
          message: errorMessage,
          tips: helpTips,
          details: data.error_details || null
        });
        setCurrentStep('upload');
      }
    } catch (err) {
      setError(err.message);
      setCurrentStep('upload');
    } finally {
      clearInterval(progressInterval);
      setIsLoading(false);
    }
  };

  // Handle lead capture
  const handleLeadCapture = async () => {
    if (!sessionId) return;
    
    try {
      // Use tenant-based API URL
      const tenant = getCurrentTenantSlug();
      const baseUrl = getTenantApiUrl(tenant);
      const response = await fetch(`${baseUrl}/ai/dental-checkup/lead-capture/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          ...leadForm
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Show success message or redirect
        alert('Thank you! We\'ll be in touch soon.');
      } else {
        throw new Error(data.message || 'Failed to capture lead information');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Reset to start over
  const resetCheckup = () => {
    setCurrentStep('upload');
    setSelectedImage(null);
    setImagePreview(null);
    setAnalysisResults(null);
    setSessionId(null);
    setError(null);
    setAnalysisProgress(0);
    setLeadForm({ name: '', email: '', phone: '', wantsConsultation: false });
  };

  // Navigation functions
  const goToStep = (step) => {
    setCurrentStep(step);
  };

  const goBack = () => {
    switch (currentStep) {
      case 'analyzing':
        setCurrentStep('upload');
        break;
      case 'results':
        setCurrentStep('analyzing');
        break;
      case 'lead-capture':
        setCurrentStep('results');
        break;
      default:
        break;
    }
  };

  // Render trust indicators
  const renderTrustIndicators = () => (
    <div className="trust-indicators">
      <div className="trust-grid">
        {trustIndicators.map((indicator, index) => (
          <div key={index} className="trust-item">
            <div className="trust-icon">
              {indicator.icon}
            </div>
            <div className="trust-content">
              <div className="trust-label">{indicator.label}</div>
              <div className="trust-value">{indicator.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render step navigation
  const renderStepNavigation = () => {
    const steps = [
      { id: 'upload', label: 'Upload', icon: Upload, active: currentStep === 'upload' },
      { id: 'analyzing', label: 'Analyzing', icon: Clock, active: currentStep === 'analyzing' },
      { id: 'results', label: 'Results', icon: CheckCircle, active: currentStep === 'results' },
      { id: 'lead-capture', label: 'Report', icon: FileText, active: currentStep === 'lead-capture' }
    ];

    return (
      <div className="step-navigation">
        <div className="step-progress">
          {steps.map((step, index) => (
            <div key={step.id} className={`step-item ${step.active ? 'active' : ''} ${currentStep === step.id ? 'current' : ''}`}>
              <div className="step-icon">
                <step.icon size={20} />
              </div>
              <div className="step-label">{step.label}</div>
              {index < steps.length - 1 && <div className="step-connector" />}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render upload step
  const renderUploadStep = () => (
    <div className="checkup-upload">
      <div className="upload-header">
        <div className="hero-section">
          <div className="hero-badge">
            <Star size={16} />
            <span>AI-Powered Dental Analysis</span>
          </div>
          <h1>Instant Dental Checkup</h1>
          <p className="hero-subtitle">Get your smile health score in just 2-3 minutes with our advanced AI technology!</p>
        </div>
        
        {renderTrustIndicators()}
        
        {/* Important Disclaimer - Collapsible */}
        <div className="disclaimer-section">
          <div className="disclaimer-header" onClick={() => setDisclaimerOpen(!disclaimerOpen)}>
            <div className="disclaimer-title">
              <AlertCircle size={20} className="disclaimer-icon" />
              <span>Important Disclaimer</span>
            </div>
            <div className={`disclaimer-toggle ${disclaimerOpen ? 'open' : ''}`}>
              <span>{disclaimerOpen ? '−' : '+'}</span>
            </div>
          </div>
          
          {disclaimerOpen && (
            <div className="disclaimer-content">
              <div className="disclaimer-main">
                <div className="disclaimer-alert">
                  <span className="alert-icon">⚠️</span>
                  <p><strong>This is NOT a medical diagnosis.</strong> Our AI analysis is for educational and screening purposes only.</p>
                </div>
                
                <div className="disclaimer-points">
                  <h4>Key Points:</h4>
                  <ul>
                    <li>Results may vary based on image quality and lighting</li>
                    <li>Different photos of the same teeth may produce different results</li>
                    <li>AI analysis cannot replace professional dental examination</li>
                    <li>Always consult a qualified dentist for proper diagnosis and treatment</li>
                  </ul>
                </div>
              </div>
              
              {/* Photo Consistency Warning */}
              <div className="photo-consistency-warning">
                <div className="warning-header">
                  <span className="warning-icon">📸</span>
                  <h4>Photo Consistency Warning</h4>
                </div>
                <p><strong>For best results and consistency:</strong></p>
                <div className="consistency-tips">
                  <div className="tip-item">
                    <span className="tip-icon">💡</span>
                    <span>Use the same lighting conditions for follow-up photos</span>
                  </div>
                  <div className="tip-item">
                    <span className="tip-icon">📐</span>
                    <span>Take photos from similar angles and distances</span>
                  </div>
                  <div className="tip-item">
                    <span className="tip-icon">🔍</span>
                    <span>Ensure teeth are clearly visible and in focus</span>
                  </div>
                  <div className="tip-item">
                    <span className="tip-icon">☀️</span>
                    <span>Avoid shadows, glare, or blurry images</span>
                  </div>
                  <div className="tip-item">
                    <span className="tip-icon">⚠️</span>
                    <span>Results may vary significantly between different photos</span>
                  </div>
                </div>
              </div>
              
              <div className="disclaimer-acknowledgment">
                <p>By using this service, you acknowledge that this is not medical advice and results are for informational purposes only.</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="upload-options">
        <div className="upload-option upload-photo">
          <div className="upload-icon">
            <Upload size={48} />
          </div>
          <div className="upload-content">
            <h3>Upload Photo</h3>
            <p>Upload a clear photo of your teeth from your device</p>
            <div className="upload-features">
              <span className="feature">📱 From your phone</span>
              <span className="feature">💻 From your computer</span>
              <span className="feature">☁️ From cloud storage</span>
            </div>
            
            {/* Drag and Drop Area */}
            <div 
              className={`drag-drop-area ${dragActive ? 'drag-active' : ''} ${imagePreview ? 'has-image' : ''}`}
              onDragEnter={handleDragIn}
              onDragLeave={handleDragOut}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {!imagePreview ? (
                <>
                  <div className="drag-icon">
                    <Upload size={32} />
                  </div>
                  <p className="drag-text">Drag & drop your image here</p>
                  <p className="drag-subtext">or click to browse</p>
                  <button 
                    className="btn btn-primary upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose File
                  </button>
                </>
              ) : (
                <div className="drag-success">
                  <CheckCircle size={32} />
                  <p>Image uploaded successfully!</p>
                </div>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
        </div>
        
        <div className="upload-option take-photo">
          <div className="upload-icon">
            <Camera size={48} />
          </div>
          <div className="upload-content">
            <h3>Take Photo</h3>
            <p>Use your camera to take a photo right now</p>
            <div className="upload-features">
              <span className="feature">📸 Instant capture</span>
              <span className="feature">✨ Real-time preview</span>
              <span className="feature">🎯 Perfect angle</span>
            </div>
            <button 
              className="btn btn-secondary camera-btn"
              onClick={handleCameraCapture}
            >
              Open Camera
            </button>
          </div>
        </div>
      </div>
      
      {/* Helpful Tips Section */}
      <div className="helpful-tips-section">
        <div className="tips-header">
          <span className="tips-icon">💡</span>
          <h4>Tips for Best Results</h4>
        </div>
        <div className="tips-grid">
          <div className="tip-card">
            <span className="tip-icon">📱</span>
            <h5>Good Lighting</h5>
            <p>Use natural light or bright, even lighting</p>
          </div>
          <div className="tip-card">
            <span className="tip-icon">🔍</span>
            <h5>Clear Focus</h5>
            <p>Ensure teeth are sharp and clearly visible</p>
          </div>
          <div className="tip-card">
            <span className="tip-icon">📐</span>
            <h5>Right Angle</h5>
            <p>Take photo from front or slightly side view</p>
          </div>
          <div className="tip-card">
            <span className="tip-icon">🚫</span>
            <h5>Avoid Shadows</h5>
            <p>Minimize shadows and glare on teeth</p>
          </div>
        </div>
      </div>
      
      {imagePreview && (
        <div className="image-preview">
          <div className="preview-header">
            <h3>✅ Image Selected</h3>
            <p>Your image is ready for analysis</p>
          </div>
          <div className="preview-content">
            <div className="image-container">
              <img src={imagePreview} alt="Preview" />
              <div className="image-overlay">
                <span className="overlay-text">Ready for Analysis</span>
              </div>
            </div>
            <div className="preview-actions">
              <button 
                className="btn btn-success start-analysis-btn"
                onClick={startAnalysis}
                disabled={isLoading}
              >
                {isLoading ? 'Starting...' : '🚀 Start Analysis'}
              </button>
              <button 
                className="btn btn-outline"
                onClick={() => {
                  setSelectedImage(null);
                  setImagePreview(null);
                  setError(null);
                }}
              >
                Choose Different Image
              </button>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="error-message">
          <div className="error-header">
            <AlertCircle size={20} />
            <span className="error-title">Analysis Failed</span>
          </div>
          <div className="error-content">
            <p className="error-text">{error.message}</p>
            
            {error.tips && error.tips.length > 0 && (
              <div className="help-tips">
                <h4>💡 Helpful Tips:</h4>
                <ul>
                  {error.tips.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {error.details && (
              <div className="error-details">
                <h4>🔍 Technical Details:</h4>
                <p>{error.details}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Render analyzing step
  const renderAnalyzingStep = () => (
    <div className="checkup-analyzing">
      <div className="analyzing-content">
        <div className="analyzing-icon">
          <Clock size={64} />
        </div>
        <h2>Analyzing Your Smile...</h2>
        <p>Our AI is examining your dental image with advanced computer vision</p>
        
        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${analysisProgress}%` }}
            />
          </div>
          <div className="progress-text">{Math.round(analysisProgress)}% Complete</div>
        </div>
        
        <div className="loading-spinner"></div>
        <p className="processing-time">This usually takes 2-3 minutes</p>
        
        {/* Analysis Steps */}
        <div className="analysis-steps">
          <div className="step-item">
            <div className="step-icon">
              <CheckCircle size={20} />
            </div>
            <span>Image uploaded and validated</span>
          </div>
          <div className={`step-item ${analysisProgress > 25 ? 'completed' : ''}`}>
            <div className="step-icon">
              {analysisProgress > 25 ? <CheckCircle size={20} /> : <Clock size={20} />}
            </div>
            <span>AI preprocessing and enhancement</span>
          </div>
          <div className={`step-item ${analysisProgress > 50 ? 'completed' : ''}`}>
            <div className="step-icon">
              {analysisProgress > 50 ? <CheckCircle size={20} /> : <Clock size={20} />}
            </div>
            <span>Dental feature detection</span>
          </div>
          <div className={`step-item ${analysisProgress > 75 ? 'completed' : ''}`}>
            <div className="step-icon">
              {analysisProgress > 75 ? <CheckCircle size={20} /> : <Clock size={20} />}
            </div>
            <span>Analysis and scoring</span>
          </div>
        </div>
        
        {/* Analysis Disclaimer */}
        <div className="analysis-disclaimer">
          <div className="disclaimer-header">
            <AlertCircle size={16} />
            <span>Analysis in Progress</span>
          </div>
          <div className="disclaimer-content">
            <p><strong>⏳ Please wait while we analyze your image:</strong></p>
            <ul>
              <li>AI is processing your dental photo</li>
              <li>Analysis quality depends on image clarity</li>
              <li>Results are estimates based on visible features</li>
              <li>Professional consultation is still recommended</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Render results step
  const renderResultsStep = () => (
    <div className="checkup-results">
      <div className="results-header">
        <div className="hero-section">
          <div className="hero-badge success">
            <CheckCircle size={16} />
            <span>Analysis Complete</span>
          </div>
          <h2>Your Dental Checkup Results</h2>
          <p>Here's what our AI detected in your smile</p>
        </div>
        
        {/* Results Disclaimer */}
        <div className="results-disclaimer">
          <div className="disclaimer-header">
            <AlertCircle size={18} />
            <span>Results Disclaimer</span>
          </div>
          <div className="disclaimer-content">
            <p><strong>📋 Remember:</strong> This analysis is based on a single photo and may not capture all dental conditions.</p>
            <ul>
              <li>Image quality affects accuracy - better lighting = better results</li>
              <li>Some conditions may not be visible in photos</li>
              <li>Professional dental examination is always recommended</li>
              <li>Results are estimates and should not be used for treatment decisions</li>
            </ul>
            
            {/* Analysis Limitations */}
            <div className="analysis-limitations">
              <h4>🔍 What Photos Cannot Detect:</h4>
              <ul>
                <li>Internal tooth decay or root problems</li>
                <li>Gum disease below the surface</li>
                <li>Bone loss or jaw issues</li>
                <li>Hidden cavities between teeth</li>
                <li>Early-stage dental problems</li>
                <li>X-ray only conditions</li>
              </ul>
              <p><strong>Note:</strong> Professional dental examination with X-rays and clinical evaluation is essential for complete diagnosis.</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="results-content">
        {analysisResults.smile_score_display && (
          <div className="smile-score-section">
            <h3>Your Smile Health Score</h3>
            <div className="score-display">
              <div className="score-circle">
                <span className="score-number">{analysisResults.smile_score_display}</span>
                <span className="score-max">/100</span>
              </div>
              <div className="score-label">
                {analysisResults.smile_score && analysisResults.smile_score >= 80 ? 'Excellent!' : 
                 analysisResults.smile_score && analysisResults.smile_score >= 60 ? 'Good' : 'Needs Attention'}
              </div>
              <div className="score-description">
                {analysisResults.smile_score && analysisResults.smile_score >= 80 ? 
                  'Your smile is in great condition! Keep up the good oral hygiene.' :
                 analysisResults.smile_score && analysisResults.smile_score >= 60 ? 
                  'Your smile is doing well, but there\'s room for improvement.' :
                  'Your smile needs some attention. Consider consulting a dentist.'}
              </div>
            </div>
          </div>
        )}
        
        {analysisResults.help_tips && analysisResults.help_tips.length > 0 && (
          <div className="success-tips">
            <h4>💡 Helpful Information:</h4>
            <ul>
              {analysisResults.help_tips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>
        )}
        
        <div className="findings-section">
          <h3>Detected Issues</h3>
          <div className="findings-list">
            {analysisResults.yolo_findings && analysisResults.yolo_findings.length > 0 ? (
              analysisResults.yolo_findings.map((finding, index) => (
                <div key={index} className="finding-item">
                  <div className="finding-icon">
                    <AlertCircle size={20} />
                  </div>
                  <div className="finding-details">
                    <span className="finding-issue">{finding.issue}</span>
                    <span className="finding-confidence">
                      {Math.round(finding.confidence * 100)}% confidence
                    </span>
                    {finding.tooth_number && (
                      <span className="finding-tooth">Tooth #{finding.tooth_number}</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-findings">
                <CheckCircle size={24} />
                <p>No specific issues detected. Your smile appears to be healthy!</p>
              </div>
            )}
          </div>
        </div>
        
        {analysisResults.explanation && (
          <div className="explanation-section">
            <h3>What This Means</h3>
            <p>{analysisResults.explanation}</p>
          </div>
        )}
        
        {analysisResults.recommendations && (
          <div className="recommendations-section">
            <h3>Recommendations</h3>
            <p>{analysisResults.recommendations}</p>
          </div>
        )}
        
        {analysisResults.processed_image && (
          <div className="annotated-image-section">
            <h3>Analysis Results</h3>
            <img 
              src={analysisResults.processed_image} 
              alt="Annotated dental image" 
              className="annotated-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <p style={{ display: 'none' }}>Image could not be loaded</p>
          </div>
        )}
      </div>
      
      <div className="results-actions">
        <button 
          className="btn btn-primary"
          onClick={() => setCurrentStep('lead-capture')}
        >
          Get Detailed Report
        </button>
        <button 
          className="btn btn-secondary"
          onClick={resetCheckup}
        >
          Start New Checkup
        </button>
      </div>
    </div>
  );

  // Render lead capture step
  const renderLeadCaptureStep = () => (
    <div className="checkup-lead-capture">
      <div className="lead-capture-header">
        <div className="hero-section">
          <div className="hero-badge">
            <FileText size={16} />
            <span>Get Your Report</span>
          </div>
          <h2>Get Your Detailed Report</h2>
          <p>Enter your details to receive a comprehensive dental health report</p>
        </div>
      </div>
      
      <div className="lead-form">
        <div className="form-group">
          <label htmlFor="name">Full Name</label>
          <input
            type="text"
            id="name"
            value={leadForm.name}
            onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
            placeholder="Enter your full name"
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            value={leadForm.email}
            onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
            placeholder="Enter your email"
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="phone">Phone Number (Optional)</label>
          <input
            type="tel"
            id="phone"
            value={leadForm.phone}
            onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
            placeholder="Enter your phone number"
          />
        </div>
        
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={leadForm.wantsConsultation}
              onChange={(e) => setLeadForm({...leadForm, wantsConsultation: e.target.checked})}
            />
            <span className="checkmark"></span>
            I would like to book a free consultation
          </label>
        </div>
        
        <div className="form-actions">
          <button 
            className="btn btn-primary"
            onClick={handleLeadCapture}
            disabled={!leadForm.name || !leadForm.email}
          >
            Get Report & Continue
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setCurrentStep('results')}
          >
            Back to Results
          </button>
        </div>
      </div>
      
      <div className="privacy-notice">
        <p>
          <small>
            Your information is secure and will only be used to provide your dental health report. 
            This is not a medical diagnosis and should not replace professional dental evaluation.
          </small>
        </p>
        
        {/* Final Disclaimer */}
        <div className="final-disclaimer">
          <div className="disclaimer-header">
            <AlertCircle size={16} />
            <span>Final Reminder</span>
          </div>
          <div className="disclaimer-content">
            <p><strong>🔒 Privacy & Medical Disclaimer:</strong></p>
            <ul>
              <li>Your data is encrypted and stored securely</li>
              <li>We do not share personal information with third parties</li>
              <li>This AI analysis is for screening purposes only</li>
              <li>For any dental concerns, please consult a licensed dentist</li>
              <li>Emergency dental issues require immediate professional attention</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Main render
  return (
    <div className="dental-checkup">
      <div className="checkup-container">
        {/* Step Navigation */}
        {renderStepNavigation()}
        
        {/* Main Content */}
        {currentStep === 'upload' && renderUploadStep()}
        {currentStep === 'analyzing' && renderAnalyzingStep()}
        {currentStep === 'results' && renderResultsStep()}
        {currentStep === 'lead-capture' && renderLeadCaptureStep()}
        
        {/* Navigation Actions */}
        <div className="navigation-actions">
          {currentStep !== 'upload' && (
            <button 
              className="btn btn-outline back-btn"
              onClick={goBack}
            >
              <ArrowLeft size={16} />
              Back
            </button>
          )}
          
          {currentStep === 'upload' && imagePreview && (
            <button 
              className="btn btn-primary next-btn"
              onClick={startAnalysis}
              disabled={isLoading}
            >
              Continue
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
      
      {/* Hidden camera element */}
      <video
        ref={cameraRef}
        style={{ display: 'none' }}
        autoPlay
        playsInline
        muted
      />
      
      {/* Camera capture modal */}
      {cameraRef.current?.srcObject && (
        <div className="camera-modal">
          <div className="camera-modal-content">
            <div className="camera-modal-header">
              <h3>Take a Photo</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  const stream = cameraRef.current.srcObject;
                  if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                  }
                }}
              >
                ×
              </button>
            </div>
            <video
              ref={cameraRef}
              autoPlay
              playsInline
              muted
              className="camera-preview"
            />
            <div className="camera-controls">
              <button 
                className="btn btn-primary"
                onClick={capturePhoto}
              >
                Capture Photo
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  const stream = cameraRef.current.srcObject;
                  if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                  }
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DentalCheckup;

